import { IFileAttachment } from '../../../../extViewmodels';

/**
 * One copy in a list of media. The same attachment can appear more than once, as a picture picked twice
 * from the image library does, so its id alone does not say which copy an edit meant. Naming the copy by
 * how many of the same attachment come before it survives other media being added or removed while an
 * edit is in flight, where a bare position would not.
 */
export interface IMediaEntry {
	copy: number;
	id: string;
}

export const getMediaEntry = (media: readonly IFileAttachment[], index: number): IMediaEntry => {
	const { id } = media[index];
	return { copy: media.slice(0, index).filter(attachment => attachment.id === id).length, id };
};

const findEntryIndex = (media: readonly IFileAttachment[], entry: IMediaEntry) => {
	let copy = 0;
	for (let index = 0; index < media.length; index++) {
		if (media[index].id !== entry.id) {
			continue;
		}
		if (copy === entry.copy) {
			return index;
		}
		copy++;
	}
	return -1;
};

/** The media with that one copy swapped out, or the same media when the copy is gone. */
export const replaceMediaEntry = (
	media: readonly IFileAttachment[],
	entry: IMediaEntry,
	replacement: IFileAttachment
): readonly IFileAttachment[] => {
	const index = findEntryIndex(media, entry);
	if (index < 0) {
		return media;
	}
	return media.map((attachment, position) => (position === index ? replacement : attachment));
};

/** The media without that one copy, or the same media when the copy is gone. */
export const removeMediaEntry = (media: readonly IFileAttachment[], entry: IMediaEntry): readonly IFileAttachment[] => {
	const index = findEntryIndex(media, entry);
	return index < 0 ? media : media.filter((_, position) => position !== index);
};
